import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, ArrowUpRightSquare, Upload, Sparkles, ShieldCheck,
  Loader2, Package, Copy, ArrowRight, AlertTriangle, Clock,
} from "lucide-react";

/**
 * MembershipPurchase — the funnel where a partner picks a Business Starter
 * Kit and files a GCash/Maya receipt. Reuses the manual-verification pattern
 * that used to power the legacy Cash-In Gate: partner submits reference +
 * receipt image, admin approves, membership flips to active, withdrawals
 * unlock.
 */

const CHANNELS = [
  { key: "gcash", label: "GCash", note: "Send via Express Send or QR." },
  { key: "maya", label: "Maya / PayMaya", note: "Send via Maya or InstaPay." },
];

// Same GCash/Maya recipient we used before — read from the settings collection
// in a future pass, but hardcoded here to keep the flow self-contained.
const CASHIN_ACCOUNT_NAME = "Eulaine May Nimer";
const CASHIN_MOBILE = "09308795489";

export default function MembershipPurchase() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(null);
  const [step, setStep] = useState("choose"); // choose | pay | verify
  const [form, setForm] = useState({
    channel: "gcash",
    reference_number: "",
    sender_name: user?.full_name || "",
    receipt_data: "",
    receipt_preview: "",
  });
  const [busy, setBusy] = useState(false);

  const { data: tiersData } = useQuery({
    queryKey: ["membership-tiers"],
    queryFn: async () => (await api.get("/membership/tiers")).data,
    staleTime: 60_000,
  });
  const tiers = tiersData?.tiers || [];

  const { data: mine, refetch: refetchMine } = useQuery({
    queryKey: ["my-membership"],
    queryFn: async () => (await api.get("/membership/mine")).data,
    staleTime: 15_000,
  });

  // Short-circuit if already active — no need to buy again.
  useEffect(() => {
    if (mine?.status === "active") {
      // Small delay so the user briefly sees the "active" state before jumping away.
      const t = setTimeout(() => navigate("/dashboard"), 1000);
      return () => clearTimeout(t);
    }
  }, [mine?.status, navigate]);

  const tier = useMemo(
    () => tiers.find((t) => t.tier === selectedTier),
    [tiers, selectedTier]
  );

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3_000_000) {
      toast.error("Receipt image must be under 3MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setForm((prev) => ({ ...prev, receipt_data: dataUrl, receipt_preview: dataUrl }));
    };
    reader.readAsDataURL(f);
  };

  const copyMobile = async () => {
    try { await navigator.clipboard.writeText(CASHIN_MOBILE); toast.success("Mobile number copied."); }
    catch { toast.error("Copy failed — long-press to copy manually."); }
  };

  const submit = async () => {
    if (!tier) return;
    if (!form.reference_number.trim() || form.reference_number.trim().length < 6) {
      toast.error("Reference number is required (≥6 characters).");
      return;
    }
    if (!form.sender_name.trim()) {
      toast.error("Sender name is required.");
      return;
    }
    if (!form.receipt_data) {
      toast.error("Please attach a clear photo/screenshot of the receipt.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/membership/purchase", {
        tier: tier.tier,
        channel: form.channel,
        reference_number: form.reference_number.trim(),
        sender_name: form.sender_name.trim(),
        amount_paid: tier.price_php,
        receipt_image: form.receipt_data,
      });
      toast.success("Kit purchase submitted. Admin will approve within one business day.");
      await refresh?.();
      await refetchMine();
      setStep("verify");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  // Pending / already-active states
  if (mine?.status === "pending") {
    return (
      <PendingState mine={mine} tiers={tiers} />
    );
  }
  if (mine?.status === "active") {
    return (
      <div className="glass-card gold-border p-10 text-center max-w-2xl mx-auto" data-testid="membership-active">
        <CheckCircle2 size={40} className="text-emerald-300 mx-auto" />
        <h1 className="mt-4 font-display text-3xl font-black">Membership active.</h1>
        <p className="text-white/70 mt-2">
          Your {mine.tier} kit is active. Redirecting to your dashboard…
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="membership-purchase-page">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">
          <Sparkles size={12} /> Activate Your Membership
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight max-w-3xl">
          Pick your Business Starter Kit to{" "}
          <span className="text-[#FBBF24]">unlock commissions & withdrawals.</span>
        </h1>
        <p className="text-white/65 mt-3 max-w-3xl leading-relaxed">
          Commissions still accrue while your membership is inactive, but Zenith holds all withdrawals
          until your kit is admin-approved. Get activated in under a day — one manual transfer, one
          receipt upload, done.
        </p>
      </div>

      {/* STEP 1 — Choose */}
      <StepBadge active={step === "choose"} done={step !== "choose"} label="1. Choose a kit" />
      <div className="grid gap-4 md:grid-cols-3">
        {tiers.map((t) => {
          const isSel = selectedTier === t.tier;
          return (
            <button
              key={t.tier}
              onClick={() => { setSelectedTier(t.tier); setStep("pay"); }}
              className={`glass-card p-5 text-left transition ${
                isSel
                  ? "border-2 border-[#FBBF24] shadow-[0_0_24px_rgba(251,191,36,0.35)]"
                  : "border border-white/10 hover:border-[#FBBF24]/50"
              }`}
              data-testid={`kit-select-${t.tier}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold">{t.tier === "starter" ? "Entry Kit" : t.tier === "builder" ? "Growth Kit" : "Executive Kit"}</div>
                {isSel && <CheckCircle2 size={16} className="text-[#FBBF24]" />}
              </div>
              <div className="font-display text-xl font-black mt-1">{t.name}</div>
              <div className="text-[#FBBF24] font-mono-num text-3xl font-black mt-3">
                ₱{t.price_php.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[11px] text-white/45 mt-1">
                Up to {t.retail_margin_pct}% retail margin · {t.products_included} products
              </div>
              <ul className="mt-4 space-y-1.5 text-xs text-white/70">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 size={11} className="text-[#FBBF24] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {/* STEP 2 — Pay via GCash / Maya */}
      {tier && (
        <>
          <StepBadge active={step === "pay"} done={step === "verify"} label="2. Send payment via GCash or Maya" />
          <div className="glass-card gold-border p-6" data-testid="payment-instructions">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">Transfer Details</div>
                <div className="space-y-3">
                  <Detail label="Amount to send" value={`₱${tier.price_php.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} highlight />
                  <Detail label="Recipient name" value={CASHIN_ACCOUNT_NAME} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold mb-1">Mobile number</div>
                    <div className="flex items-center gap-2">
                      <div className="font-mono-num text-lg text-[#FBBF24]">{CASHIN_MOBILE}</div>
                      <button onClick={copyMobile} className="text-[10px] px-2 py-1 rounded border border-white/15 hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1" data-testid="copy-mobile">
                        <Copy size={11} /> Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-l border-white/10 pl-6 md:block">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">Rules</div>
                <ul className="space-y-2 text-xs text-white/70 leading-relaxed">
                  <li className="flex items-start gap-2"><ShieldCheck size={12} className="text-[#FBBF24] mt-0.5 shrink-0" /> Only GCash and PayMaya are accepted.</li>
                  <li className="flex items-start gap-2"><ShieldCheck size={12} className="text-[#FBBF24] mt-0.5 shrink-0" /> Sender name should match your registered name.</li>
                  <li className="flex items-start gap-2"><AlertTriangle size={12} className="text-[#FCA5A5] mt-0.5 shrink-0" /> A duplicate reference number is auto-rejected.</li>
                  <li className="flex items-start gap-2"><Clock size={12} className="text-[#FBBF24] mt-0.5 shrink-0" /> Approvals happen within one business day.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* STEP 3 — File receipt */}
          <StepBadge active={step === "pay"} done={step === "verify"} label="3. Upload receipt & submit" />
          <div className="glass-card p-6 space-y-4" data-testid="receipt-form">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="zfd-label">Payment channel</label>
                <div className="flex gap-2">
                  {CHANNELS.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => setForm({ ...form, channel: c.key })}
                      className={`flex-1 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-widest border ${
                        form.channel === c.key
                          ? "border-[#FBBF24] text-[#FBBF24] bg-[#FBBF24]/8"
                          : "border-white/15 text-white/60 hover:border-white/30"
                      }`}
                      data-testid={`channel-${c.key}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="zfd-label">Reference number</label>
                <input
                  className="zfd-input"
                  value={form.reference_number}
                  onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                  placeholder="e.g. 1234567890"
                  data-testid="input-reference"
                />
              </div>
              <div className="md:col-span-2">
                <label className="zfd-label">Sender name (as it appears on your GCash/Maya receipt)</label>
                <input
                  className="zfd-input"
                  value={form.sender_name}
                  onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                  data-testid="input-sender"
                />
              </div>
            </div>
            <div>
              <label className="zfd-label">Receipt image (PNG/JPEG, ≤3MB)</label>
              <label className="flex items-center gap-3 cursor-pointer px-4 py-3 border border-dashed border-white/20 rounded-md hover:border-[#FBBF24] hover:bg-[#FBBF24]/[0.02]" data-testid="input-receipt-label">
                <Upload size={16} className="text-[#FBBF24]" />
                <span className="text-sm text-white/70">
                  {form.receipt_data ? "Receipt attached. Click to replace." : "Click to attach a receipt screenshot"}
                </span>
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="input-receipt-file" />
              </label>
              {form.receipt_preview && (
                <img src={form.receipt_preview} alt="Receipt preview" className="mt-3 max-h-52 rounded-md border border-white/10" />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                disabled={busy}
                onClick={submit}
                className="btn-deploy inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="submit-kit"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRightSquare size={14} />}
                {busy ? "Submitting…" : `Submit ₱${tier.price_php.toLocaleString()} Kit Purchase`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StepBadge({ active, done, label }) {
  return (
    <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold ${done ? "text-emerald-300" : active ? "text-[#FBBF24]" : "text-white/40"}`}>
      {done ? <CheckCircle2 size={13} /> : <div className={`w-3 h-3 rounded-full ${active ? "bg-[#FBBF24]" : "border border-white/30"}`} />}
      {label}
    </div>
  );
}

function Detail({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold mb-0.5">{label}</div>
      <div className={`${highlight ? "text-2xl font-mono-num text-[#FBBF24] font-bold" : "text-white text-lg"}`}>{value}</div>
    </div>
  );
}

function PendingState({ mine, tiers }) {
  const tier = tiers.find((t) => t.tier === mine?.pending?.tier) || {};
  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="membership-pending">
      <div className="glass-card gold-border p-8 text-center">
        <Clock size={40} className="text-[#FBBF24] mx-auto" />
        <h1 className="mt-4 font-display text-3xl font-black">Your kit is under review.</h1>
        <p className="text-white/70 mt-2">
          We received your ₱{Number(mine?.pending?.amount_paid || 0).toLocaleString()} payment for{" "}
          <strong>{tier.name || mine?.pending?.tier}</strong>. An admin will verify against our GCash/Maya records
          within one business day.
        </p>
        <div className="mt-6 text-sm text-white/60 space-y-2">
          <div>Reference: <span className="font-mono-num text-white">{mine?.pending?.reference_number}</span></div>
          <div>Filed: <span className="text-white/80">{mine?.pending?.submitted_at?.slice(0, 16)?.replace("T", " ")}</span></div>
        </div>
      </div>
      <div className="text-center">
        <a href="/dashboard" className="text-xs uppercase tracking-widest font-bold text-white/50 hover:text-[#FBBF24] inline-flex items-center gap-1" data-testid="pending-back-dash">
          Back to dashboard <ArrowRight size={12} />
        </a>
      </div>
    </div>
  );
}
